import { useRef, useState } from "react"
import { Check, Copy } from "lucide-react"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { useToast } from "@/components/ui/use-toast"

// Radix Toast turns a drag into swipe-to-dismiss, so error text can't be selected.
// Error toasts get a copy button instead.
function CopyToastText({ getText }: { getText: () => string }) {
  const [copied, setCopied] = useState(false)

  return (
    <button
      type="button"
      aria-label="Copy message"
      title="Copy message"
      className="absolute right-1 bottom-1 rounded-md p-1 text-red-50/70 transition-colors hover:text-red-50 focus:outline-none focus:ring-1 focus:ring-red-400"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(getText())
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          // Clipboard blocked (insecure context or permission); nothing to do
        }
      }}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  )
}

function ToastItem({
  title,
  description,
  action,
  ...props
}: Omit<ReturnType<typeof useToast>["toasts"][number], "id">) {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <Toast {...props}>
      <div ref={contentRef} className="grid gap-1">
        {title && <ToastTitle>{title}</ToastTitle>}
        {description && (
          <ToastDescription>{description}</ToastDescription>
        )}
      </div>
      {action}
      {props.variant === "destructive" && (
        <CopyToastText getText={() => contentRef.current?.innerText.trim() ?? ""} />
      )}
      <ToastClose />
    </Toast>
  )
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(function ({ id, ...toast }) {
        return <ToastItem key={id} {...toast} />
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
